import { ModalShell } from '@lokalise/louis'
import { useAssignAiConfigurationToProjectsMutation } from '@lokalise/shared'
import type {
  AiConfigLanguage,
  AiConfigProject,
  RagProfileConfig,
} from '@lokalise/translation-api-schemas'
import { RagAnalyticsEventName } from '@lokalise/types'
import { useEffect, useState } from 'react'
import { useUpdateProfile } from '../../hooks/useUpdateProfile'
import { useRagAnalytics } from '../../providers/analytics'
import { type Conflict, isConflictErrorResponse, isWretchError } from '../../types/api'
import {
  appHttpClient,
  authenticatedPublicApiHeaderBuilder,
  getTeamIdFromSession,
} from '../../utils/api'
import { formatDateForApi } from '../../utils/date'
import { showUnexpectedErrorToast } from '../../utils/toast'
import { OverrideStep } from '../OverrideStep/OverrideStep'
import { StepController } from '../StepController/StepController'
import styles from './AssignmentModal.module.css'
import { ActivationStep } from './components/ActivationStep/ActivationStep'
import { OverviewStep } from './components/OverviewStep/OverviewStep'
import { TmBasicSettingsStep } from './components/ProfileBasicsStep/TmBasicSettingsStep'
import { TMDataSourceStep } from './components/TMDataSourceStep/TMDataSourceStep'

type AssignmentModalProps = {
  onClose: () => void
  onActivateSuccess: () => void
  initialDate?: Date
  initialSourceLang?: AiConfigLanguage
  initialTargetLangs?: AiConfigLanguage[]
  initialProjects?: AiConfigProject[]
  profile: RagProfileConfig
  isEdit: boolean
}

export const TmProfileName = 'AI profile - TM'

export const AssignmentModal = ({
  onClose,
  onActivateSuccess,
  isEdit,
  profile,
  initialDate,
  initialSourceLang,
  initialTargetLangs = [],
  initialProjects = [],
}: AssignmentModalProps) => {
  const [aiModelIntegration, setAiModelIntegration] = useState(profile?.config?.integration)
  const [sourceLang, setSourceLang] = useState<AiConfigLanguage | undefined>(initialSourceLang)
  const [targetLangs, setTargetLangs] = useState<AiConfigLanguage[]>(initialTargetLangs)
  const [date, setDate] = useState<Date | undefined>(initialDate)
  const [projects, setProjects] = useState<AiConfigProject[]>(initialProjects)

  const [stepIndex, setStepIndex] = useState(isEdit ? 1 : 0)
  const [conflicts, setConflicts] = useState<Conflict[]>([])

  const teamId = getTeamIdFromSession()

  const profileId = profile.id
  const profileType = profile.config.rag.type

  const track = useRagAnalytics()

  const onSuccess = () => onActivateSuccess()

  const onError = (error: unknown) => {
    if (isWretchError(error) && isConflictErrorResponse(error.json)) {
      setConflicts(error.json.details.conflicts)
      setStepIndex(4)
      return
    }

    showUnexpectedErrorToast()
  }

  useEffect(() => {
    track(RagAnalyticsEventName.AI_PROFILE_ACTIVATION_OPENED, {
      type: profileType,
      profile_id: profileId,
    })
  }, [profileId, profileType, track])

  const { update, isPending: isUpdatingAiConfig } = useUpdateProfile({ onSuccess, onError })

  const { mutate: assignAiConfig, isPending: isAssigningAiConfig } =
    useAssignAiConfigurationToProjectsMutation(appHttpClient, authenticatedPublicApiHeaderBuilder, {
      onSuccess,
      onError,
    })

  const onActivate = () => {
    update({
      projects: projects.map(({ id }) => id),
      profileId: profile.id,
      profile: {
        name: profile.name,
        is_default: profile.is_default,
        config: {
          integration: aiModelIntegration,
          rag: {
            ...profile.config.rag,
            source_languages: sourceLang ? [sourceLang?.id] : undefined,
            target_languages: targetLangs.map(({ id }) => id),
            created_after: date ? formatDateForApi(date) : null,
          },
        },
      },
    })
  }

  const onForceAssign = () =>
    assignAiConfig({
      project_ids: projects.map((project) => project.id),
      teamId,
      configurationId: profile.id,
      force: true,
    })

  const onNext = () => setStepIndex((i) => i + 1)

  return (
    <ModalShell className={styles.modal} opened onClose={onClose} title="TM profile assignment">
      <StepController stepIndex={stepIndex}>
        <OverviewStep profileName={TmProfileName} onClose={onClose} onNext={onNext} />
        <TmBasicSettingsStep
          profileName={TmProfileName}
          selectedAiModel={aiModelIntegration}
          onAiModelSelected={setAiModelIntegration}
          onNext={onNext}
          onClose={onClose}
        />
        <TMDataSourceStep
          date={date}
          profileName={TmProfileName}
          integration={aiModelIntegration}
          sourceLang={sourceLang}
          targetLangs={targetLangs}
          onChangeDate={setDate}
          onChangeSourceLang={setSourceLang}
          onChangeTargetLangs={setTargetLangs}
          onClose={onClose}
          onNext={onNext}
          isEdit={isEdit}
        />
        <ActivationStep
          profileName={TmProfileName}
          integration={aiModelIntegration}
          projects={projects}
          sourceLang={sourceLang}
          targetLangs={targetLangs}
          onProjectsChange={setProjects}
          onActivate={onActivate}
          onClose={onClose}
          isLoading={isUpdatingAiConfig || isAssigningAiConfig}
          isEdit={isEdit}
        />
        <OverrideStep
          conflicts={conflicts}
          profileName={profile.name}
          isOverriding={isAssigningAiConfig}
          onClose={onClose}
          onUpdate={onForceAssign}
        />
      </StepController>
    </ModalShell>
  )
}
